import { Icon } from "@iconify/react";
import { colors, PLAN_COLORS, STATE_TONE, type, viz } from "../styles";
import { formatRelative } from "../format";

/**
 * Plan identity is carried by a colored dot in the plan's categorical slot,
 * with the label itself in primary ink on a neutral chip.
 *
 * That's deliberate, not decorative: the `dataviz` rule is that text wears text
 * tokens and a colored mark beside it carries identity. Two of the plan slots
 * sit below 3:1 on white, so plan-colored text would be unreadable — and a
 * solid plan-colored chip with white text fails the same check. The dot keeps
 * the plan↔color mapping identical to every chart on the dashboard while the
 * label stays legible.
 */
export const PlanPill = ({ plan }: { plan: string }) => (
  <span
    style={{
      ...type.bodySmallMedium,
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      color: colors.ink,
      background: colors.surfaceMuted,
      padding: "3px 9px",
      borderRadius: 999,
      whiteSpace: "nowrap",
    }}
  >
    <span
      style={{
        width: 7,
        height: 7,
        borderRadius: 999,
        background: PLAN_COLORS[plan] ?? colors.inkFaint,
        flexShrink: 0,
      }}
    />
    {plan}
  </span>
);

/** Subscription state — a genuine good/bad axis, so it wears status tones. */
export const StatePill = ({ state, label }: { state: string; label?: string }) => {
  const tone = STATE_TONE[state] ?? STATE_TONE.INACTIVE;
  return (
    <span
      style={{
        ...type.bodySmallMedium,
        color: tone.color,
        background: tone.bg,
        padding: "3px 9px",
        borderRadius: 999,
        whiteSpace: "nowrap",
      }}
    >
      {label ?? tone.label}
    </span>
  );
};

export const TagChip = ({
  label,
  icon,
  tone = "neutral",
  title,
}: {
  label: string;
  icon?: string;
  tone?: "neutral" | "warning" | "critical";
  title?: string;
}) => {
  const toneColor =
    tone === "critical"
      ? colors.stateFailed
      : tone === "warning"
      ? colors.stateTrial
      : colors.inkMuted;

  return (
    <span
      title={title}
      style={{
        ...type.bodySmallMedium,
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        color: toneColor,
        border: `1px solid ${colors.border}`,
        background: colors.surface,
        padding: "2px 8px",
        borderRadius: 999,
        whiteSpace: "nowrap",
      }}
    >
      {icon && <Icon icon={icon} height={12} />}
      {label}
    </span>
  );
};

/** The muted "Placeholder" marker. Never a status color — it isn't a warning. */
export const PlaceholderChip = () => (
  <span
    style={{
      ...type.bodySmallMedium,
      fontSize: 11,
      color: colors.inkFaint,
      background: colors.surfaceMuted,
      border: `1px solid ${colors.border}`,
      padding: "2px 8px",
      borderRadius: 999,
      whiteSpace: "nowrap",
    }}
  >
    Placeholder
  </span>
);

/**
 * Every real (non-placeholder) figure on this dashboard says explicitly
 * whether it was just fetched or comes from the nightly rollup — Jonathan's
 * requirement, so two numbers that look identical are never actually on
 * different clocks. Neutral styling on both: freshness isn't a status axis,
 * "cached" isn't a warning any more than "placeholder" is.
 */
const freshnessChipStyle = {
  ...type.bodySmallMedium,
  fontSize: 11,
  display: "inline-flex" as const,
  alignItems: "center" as const,
  gap: 4,
  color: colors.inkFaint,
  background: colors.surfaceMuted,
  border: `1px solid ${colors.border}`,
  padding: "2px 8px",
  borderRadius: 999,
  whiteSpace: "nowrap" as const,
};

export const LiveChip = () => (
  <span style={freshnessChipStyle} title="Fetched fresh on this page load">
    <span
      style={{
        width: 6,
        height: 6,
        borderRadius: 999,
        background: viz.good,
        flexShrink: 0,
      }}
    />
    Live
  </span>
);

export const CachedChip = ({ updatedAt }: { updatedAt?: string }) => (
  <span
    style={freshnessChipStyle}
    title={
      updatedAt
        ? `Last updated by the nightly sync ${formatRelative(updatedAt)}`
        : "Refreshed by the nightly sync, not on page load"
    }
  >
    <Icon icon="ion:time-outline" height={11} />
    {updatedAt ? `Updated ${formatRelative(updatedAt)}` : "Nightly sync"}
  </span>
);

/** A single prop type every real (non-placeholder) card/tile takes, so every
 * new one is forced to declare its freshness rather than silently omitting it. */
export type Freshness = { kind: "live" } | { kind: "cached"; updatedAt?: string };

export const FreshnessChip = ({ freshness }: { freshness: Freshness }) =>
  freshness.kind === "live" ? <LiveChip /> : <CachedChip updatedAt={freshness.updatedAt} />;
